import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import ModalSaldoInicial from "./ModalSaldoInicial";
import ModalCobroPagoExtraEntidad from "./ModalCobroPagoExtraEntidad";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { getCtaCteEntidad } from "../helpers/rutaReportes";
import { getClientes } from "../helpers/rutaClientes";
import { getTransportes } from "../helpers/rutaTransportes";

/* ----------------------------- utils ----------------------------------- */
const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayYYYYMMDD = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const sortES = (a, b) =>
  (a || "").localeCompare(b || "", "es", { sensitivity: "base" });

const fmtFechaBA = (m) => {
  const s = m?.fechaYMD;
  if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, d] = s.split("-");
    return `${d}/${mo}/${y}`;
  }

  if (!m?.fecha) return "";
  try {
    const dt = new Date(m.fecha);
    return new Intl.DateTimeFormat("es-AR", {
      timeZone: "America/Argentina/Buenos_Aires",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(dt);
  } catch {
    const dt = new Date(m.fecha);
    return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("es-AR");
  }
};

const labelKind = (k) => {
  switch ((k || "").toUpperCase()) {
    case "REMITO":
      return "Comanda";
    case "COBRO":
      return "Cobranza";
    case "PAGO":
      return "Pago";
    case "SALDO_INICIAL":
      return "Saldo inicial";
    default:
      return k || "";
  }
};

const cleanStr = (v) => {
  if (v === false || v === true) return "";
  const s = String(v ?? "").trim();
  if (!s) return "";
  const sl = s.toLowerCase();
  if (sl === "false" || sl === "true" || sl === "null" || sl === "undefined") {
    return "";
  }
  return s;
};

const dayBefore = (ymd) => {
  if (!ymd) return "";
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};

const conceptoUI = (m) => {
  const k = (m?.kind || "").toUpperCase();
  const comp = cleanStr(m?.comprobante);
  const c = cleanStr(m?.concepto);

  if (!c) return `${labelKind(k)}${comp ? ` ${comp}` : ""}`.trim();

  if (/saldo\s+inicial/i.test(c)) return c;

  if (k === "REMITO") {
    const base = c
      ? c.replace(/^remito\b/i, "Comanda").replace(/^comanda\b/i, "Comanda")
      : "Comanda";
    if (comp && !base.includes(comp)) return `${base} ${comp}`.trim();
    return base;
  }

  if ((k === "COBRO" || k === "PAGO") && comp && !c.includes(comp)) {
    return `${c} (${comp})`;
  }

  return c;
};

const getFacturaUI = (m) =>
  cleanStr(
    m?.nroFac ??
      m?.nrofac ??
      m?.factura ??
      m?.nroFactura ??
      m?.numeroFactura ??
      m?.comprobanteFiscal ??
      m?.cbte ??
      m?.fact
  );

const getChoferUI = (m) =>
  cleanStr(
    m?.choferNombre ??
      m?.chofer?.nombre ??
      m?.chofer?.apellidoNombre ??
      m?.chofer ??
      m?.nombreChofer
  );

/* ----------------------------- component -------------------------------- */
export default function ReporteCtaCteEntidad() {
  const [showSaldo, setShowSaldo] = useState(false);
  const [showExtra, setShowExtra] = useState(false);

  const [tipo, setTipo] = useState("CLIENTE");
  const [desde, setDesde] = useState(todayYYYYMMDD());
  const [hasta, setHasta] = useState(todayYYYYMMDD());

  const [lista, setLista] = useState([]);
  const [q, setQ] = useState("");
  const [entidadId, setEntidadId] = useState("");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setErr("");
        setLista([]);
        setEntidadId("");
        setData(null);
        setQ("");

        const r =
          tipo === "CLIENTE" ? await getClientes() : await getTransportes();

        const raw =
          (tipo === "CLIENTE"
            ? r?.clientes || r?.data?.clientes
            : r?.transportes || r?.data?.transportes) ||
          r ||
          [];

        const arr = (Array.isArray(raw) ? raw : []).map((x) => ({
          id: x?._id || x?.id,
          label:
            x?.razonsocial ||
            x?.razonSocial ||
            x?.transporte ||
            x?.nombre ||
            x?.descripcion ||
            x?.apellido ||
            "SIN NOMBRE",
        }));

        arr.sort((a, b) => sortES(a.label, b.label));
        if (alive) setLista(arr);
      } catch (e) {
        if (alive) setErr(e?.message || "No se pudo cargar la lista.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [tipo]);

  const listaFiltrada = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    if (!s) return lista;
    return lista.filter((x) => (x.label || "").toLowerCase().includes(s));
  }, [lista, q]);

  const entidadLabel = useMemo(() => {
    const f = lista.find((x) => x.id === entidadId);
    return f?.label || "";
  }, [lista, entidadId]);

  const consultar = async () => {
    setErr("");
    setLoading(true);

    try {
      if (!entidadId) throw new Error("Elegí un cliente / transporte.");
      if (!desde || !hasta) throw new Error("Completá desde/hasta.");

      const r = await getCtaCteEntidad({ tipo, id: entidadId, desde, hasta });
      if (!r?.ok) {
        throw new Error(r?.error || "No se pudo cargar la cuenta corriente.");
      }

      setData(r);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const tot = data?.totalesPeriodo || {};
  const s0 = data?.saldoInicial || {};
  const sf = data?.saldoFinal || {};
  const movs = data?.movimientos || [];

  /* ----------------- lógica de marcado de filas saldadas ----------------- */
  const getNroViajeKey = (m) =>
    cleanStr(
      m?.nroviaje ??
        m?.nroViaje ??
        m?.viajeNumero ??
        m?.viajeNro ??
        m?.viaje?.nroviaje ??
        m?.viaje?.nroViaje
    );

  const toCentavos = (n) => Math.round(Number(n || 0) * 100);

  const normKey = (v) => cleanStr(v).toUpperCase();

  const isTruthyish = (v) => {
    if (v === true || v === 1) return true;
    const s = String(v ?? "").trim().toLowerCase();
    return [
      "1",
      "true",
      "si",
      "sí",
      "s",
      "yes",
      "cancelado",
      "cancelada",
      "saldado",
      "saldada",
      "pagado",
      "pagada",
      "cobrado",
      "cobrada",
      "cerrado",
      "cerrada",
      "completo",
      "completa",
      "total",
    ].includes(s);
  };

  // const getDocNumberFromText = (v) => {
  //   const s = cleanStr(v);
  //   if (!s) return "";
  //   const m = s.match(/\b(?:comanda|remito)\s*#?\s*([a-z0-9-]+)/i);
  //   return cleanStr(m?.[1]);
  // };
  const normalizeDocNumber = (v) => {
    const s = cleanStr(v);
    if (!s) return "";

    const m = s.match(/#?\s*0*([a-z0-9-]+)/i);
    const raw = cleanStr(m?.[1] || s).replace(/^0+/, "");
    return raw || "0";
  };

  const getDocNumberFromText = (v) => {
    const s = cleanStr(v);
    if (!s) return "";

    let m = s.match(/\b(?:comanda|remito)\s*#?\s*0*([a-z0-9-]+)/i);
    if (m?.[1]) return normalizeDocNumber(m[1]);

    // para valores tipo "#000015"
    m = s.match(/^#?\s*0*([a-z0-9-]+)$/i);
    if (m?.[1]) return normalizeDocNumber(m[1]);

    return "";
  };

  const addIf = (set, v) => {
    const k = normKey(v);
    if (k) set.add(k);
  };

  const addComandaKey = (set, v) => {
    const nro = normalizeDocNumber(v);
    if (!nro) return;
    set.add(`COMANDA:${nro}`);
  };

  const addViajeKey = (set, v) => {
    const s = cleanStr(v);
    if (!s) return;

    const n = Number(s);
    if (Number.isFinite(n)) {
      set.add(`VIAJE:${String(n)}`);
    } else {
      set.add(`VIAJE:${s.toUpperCase()}`);
    }
  };

  const getIdentityKeys = (x) => {
    const keys = new Set();

    // ids / referencias explícitas
    [
      x?._id,
      x?.id,
      x?.refId,
      x?.origenId,
      x?.movimientoId,
      x?.remitoId,
      x?.comandaId,
      x?.documentoId,
      x?.comprobante,
    ].forEach((v) => addIf(keys, v));

    // números de comanda / remito como claves SEGURAS
    [
      x?.comanda,
      x?.remito,
      x?.nrocomanda,
      x?.nroComanda,
      x?.numeroComanda,
      x?.nroremito,
      x?.nroRemito,
      x?.numeroRemito,
    ].forEach((v) => {
      const nro = getDocNumberFromText(v) || normalizeDocNumber(v);
      if (nro) addComandaKey(keys, nro);
    });

    // números deducidos desde texto
    [x?.concepto, x?.detalle, x?.descripcion].forEach((txt) => {
      const nro = getDocNumberFromText(txt);
      if (nro) addComandaKey(keys, nro);
    });

    // viaje explícito
    [
      x?.viaje,
      x?.nroViaje,
      x?.nroviaje,
      x?.viajeNumero,
      x?.viajeNro,
      getNroViajeKey(x),
    ].forEach((v) => addViajeKey(keys, v));

    return [...keys];
  };

  const getAplicaciones = (m) => {
    const out = [];

    const direct = [
      m?.aplicaciones,
      m?.aplicados,
      m?.itemsAplicados,
      m?.cancelaciones,
      m?.comandasCanceladas,
      m?.remitosCancelados,
      m?.comandas,
      m?.remitos,
      m?.items,
      m?.detalle,
      m?.detalles,
      m?.aplicacion,
      m?.aplicado,
      m?.cancelacion,
    ];

    direct.forEach((item) => {
      if (Array.isArray(item)) out.push(...item);
      else if (item && typeof item === "object") out.push(item);
    });

    return out;
  };

  const isComandaCancelada = (m) => {
    const kind = String(m?.kind || "").toUpperCase();
    if (kind !== "REMITO") return false;

    if (
      isTruthyish(m?.cancelada) ||
      isTruthyish(m?.cancelado) ||
      isTruthyish(m?.saldada) ||
      isTruthyish(m?.saldado) ||
      isTruthyish(m?.pagada) ||
      isTruthyish(m?.pagado) ||
      isTruthyish(m?.cobrada) ||
      isTruthyish(m?.cobrado) ||
      isTruthyish(m?.cerrada) ||
      isTruthyish(m?.cerrado) ||
      isTruthyish(m?.aplicadoCompleto) ||
      isTruthyish(m?.cancelacionTotal)
    ) {
      return true;
    }

    const estado = normKey(
      m?.estadoPago ??
        m?.estadoCobro ??
        m?.estadoCancelacion ??
        m?.estado ??
        m?.status
    );

    if (
      ["CANCELADA", "SALDADA", "PAGADA", "COBRADA", "CERRADA", "TOTAL"].includes(
        estado
      )
    ) {
      return true;
    }

    // Solo si el campo existe realmente
    const rawPendiente =
      m?.saldoPendiente ??
      m?.pendiente ??
      m?.restante ??
      m?.saldoAbierto ??
      m?.saldoComandaPendiente ??
      m?.saldo_restante;

    if (rawPendiente !== undefined && rawPendiente !== null && rawPendiente !== "") {
      const pendiente = Number(rawPendiente);
      if (Number.isFinite(pendiente) && pendiente === 0 && Number(m?.debe || 0) > 0) {
        return true;
      }
    }

    return false;
  };

    const isMovimientoCancelacion = (m) => {
    const txt = [
      conceptoUI(m),
      cleanStr(m?.concepto),
      cleanStr(m?.comprobante),
    ]
      .join(" ")
      .toLowerCase();

    return (
      txt.includes("cancel") ||
      txt.includes("orden de pago") ||
      txt.includes("orden de cobro") ||
      txt.includes("cobranza") ||
      txt.includes("pago")
    );
  };

  const filasSaldadas = useMemo(() => {
    const set = new Set();

    const remitosByKey = new Map();
    const remitosPorViaje = new Map();
    const remitosPorImporte = new Map();

    // Indexamos remitos
    movs.forEach((m, idx) => {
      const kind = String(m?.kind || "").toUpperCase();
      if (kind !== "REMITO") return;

      if (isComandaCancelada(m)) {
        set.add(idx);
      }

      getIdentityKeys(m).forEach((key) => {
        if (!key) return;
        if (!remitosByKey.has(key)) remitosByKey.set(key, new Set());
        remitosByKey.get(key).add(idx);
      });

      const nroViaje = getNroViajeKey(m);
      if (nroViaje) {
        if (!remitosPorViaje.has(nroViaje)) remitosPorViaje.set(nroViaje, []);
        remitosPorViaje.get(nroViaje).push(idx);
      }

      const importe = toCentavos(m?.debe);
      if (importe > 0) {
        if (!remitosPorImporte.has(importe)) remitosPorImporte.set(importe, []);
        remitosPorImporte.get(importe).push(idx);
      }
    });

    // Relacionamos pagos/cobros
    movs.forEach((m, idx) => {
      const kind = String(m?.kind || "").toUpperCase();
      if (kind !== "PAGO" && kind !== "COBRO") return;

      const matchedRows = new Set();
      const keys = new Set();

      // 1) Intento fuerte: referencias explícitas
      getIdentityKeys(m).forEach((k) => keys.add(k));

      getAplicaciones(m).forEach((a) => {
        if (a && typeof a === "object") {
          getIdentityKeys(a).forEach((k) => keys.add(k));
        } else {
          const v = cleanStr(a);
          if (v) {
            const nroTxt = getDocNumberFromText(v);
            if (nroTxt) {
              addComandaKey(keys, nroTxt);
            }
          }
        }
      });

      keys.forEach((key) => {
        const rows = remitosByKey.get(key);
        if (rows) rows.forEach((rowIdx) => matchedRows.add(rowIdx));
      });

      if (matchedRows.size > 0) {
        set.add(idx);
        matchedRows.forEach((rowIdx) => set.add(rowIdx));
        return;
      }

      // 2) Fallback seguro: cancelación 1 a 1 por importe exacto único
      const importeMov = toCentavos(m?.haber || m?.debe);
      if (importeMov > 0 && isMovimientoCancelacion(m)) {
        const candidatos = (remitosPorImporte.get(importeMov) || []).filter(
          (rowIdx) => rowIdx < idx
        );

        if (candidatos.length === 1) {
          set.add(idx);
          set.add(candidatos[0]);
          return;
        }
      }

      // 3) Último fallback: por viaje, solo si hay un único remito
      const nroViaje = getNroViajeKey(m);
      if (nroViaje) {
        const remitos = remitosPorViaje.get(nroViaje) || [];
        if (remitos.length === 1) {
          set.add(idx);
          set.add(remitos[0]);
        }
      }
    });

    return set;
  }, [movs]);

  const esMovimientoSaldado = (_m, idx) => filasSaldadas.has(idx);  

  const exportarPDF = async () => {
    if (!data) return;

    const LOGO_URL = "/Danlu.png";
    const EMPRESA = "Transporte DANLU S.R.L.";
    const LINEA1 = "Dirección / Localidad / Provincia";
    const LINEA2 = "Tel: xxx  •  mail@dominio.com";
    const FOOTER_TXT = "Documento electrónico generado por el sistema DANLU";

    const loadImageAsDataURL = async (url) => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const mime = blob.type || "";
        const dataUrl = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        return { dataUrl, mime };
      } catch (e) {
        console.warn("[PDF] No pude cargar logo:", e?.message || e);
        return { dataUrl: null, mime: "" };
      }
    };

    const { dataUrl: logoDataUrl, mime: logoMime } =
      await loadImageAsDataURL(LOGO_URL);

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const M_LEFT = 8;
    const M_RIGHT = 8;
    const M_TOP = 8;
    const M_BOTTOM = 8;

    const HEADER_H = 30;
    const FOOTER_H = 10;

    const title = `Cuenta Corriente (${
      tipo === "CLIENTE" ? "Cliente" : "Transporte"
    })`;
    const subtitle = `${entidadLabel || "(sin nombre)"} — Período: ${desde} a ${hasta}`;

    const drawHeader = () => {
      const y0 = M_TOP + 2;

      if (logoDataUrl) {
        const LOGO_W = 20;
        const LOGO_H = 16;
        const format = (logoMime || "").includes("jpeg") ? "JPEG" : "PNG";
        try {
          doc.addImage(logoDataUrl, format, M_LEFT, y0, LOGO_W, LOGO_H);
        } catch (e) {
          console.warn("[PDF] Logo inválido:", e?.message || e);
        }
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(EMPRESA, pageW / 2, y0 + 6, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text(LINEA1, pageW / 2, y0 + 11, { align: "center" });
      doc.text(LINEA2, pageW / 2, y0 + 15, { align: "center" });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(title, M_LEFT, y0 + 22);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(subtitle, M_LEFT, y0 + 27);

      doc.setDrawColor(180);
      doc.line(M_LEFT, M_TOP + HEADER_H, pageW - M_RIGHT, M_TOP + HEADER_H);
    };

    const drawFooter = (pageNumber, totalPages) => {
      const y = pageH - M_BOTTOM;

      doc.setDrawColor(200);
      doc.line(M_LEFT, y - FOOTER_H + 2, pageW - M_RIGHT, y - FOOTER_H + 2);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(FOOTER_TXT, M_LEFT, y - 2);
      doc.text(`Página ${pageNumber} de ${totalPages}`, pageW - M_RIGHT, y - 2, {
        align: "right",
      });
    };

    const drawResumen = (startY) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(25, 25, 25);

      const x = M_LEFT;
      let y = startY;

      doc.text(`Saldo inicial: $ ${fmtARS(s0.saldo)}`, x, y);
      y += 5;

      if (tipo === "TRANSPORTE") {
        doc.setTextColor(220, 53, 69);

        doc.text(
          `Pactado bruto del período: $ ${fmtARS(tot.pactadoBruto)}`,
          x,
          y
        );
        y += 5;

        doc.text(
          `Comisiones del período: $ ${fmtARS(tot.comision)}`,
          x,
          y
        );
        y += 5;

        doc.text(`Merma del período: $ ${fmtARS(tot.merma)}`, x, y);
        y += 5;

        doc.setTextColor(25, 25, 25);
      }

      doc.text(
        `Debe período${tipo === "TRANSPORTE" ? " (neto)" : ""}: $ ${fmtARS(tot.debe)}`,
        x,
        y
      );
      y += 5;

      doc.text(`Haber período: $ ${fmtARS(tot.haber)}`, x, y);
      y += 5;

      doc.text(`Saldo final: $ ${fmtARS(sf.saldo)}`, x, y);

      return y + 4;
    };

    const head = [[
      "Fecha",
      "Concepto",
      "Chofer",
      "Factura",
      "Nro Viaje",
      "Debe",
      "Haber",
      "Saldo",
    ]];

    const body = movs.map((m) => {
      const debe = Number(m?.debe || 0);
      const haber = Number(m?.haber || 0);

      return [
        fmtFechaBA(m),
        conceptoUI(m),
        getChoferUI(m),
        getFacturaUI(m),
        getNroViajeKey(m),
        debe ? `$ ${fmtARS(debe)}` : "",
        haber ? `$ ${fmtARS(haber)}` : "",
        `$ ${fmtARS(m?.saldo)}`,
      ];
    });

    drawHeader();
    const startYTabla = drawResumen(M_TOP + HEADER_H + 8);

    autoTable(doc, {
      startY: startYTabla,
      head,
      body,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 1.4,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fontStyle: "bold",
        fillColor: [245, 245, 245],
        textColor: [20, 20, 20],
        lineColor: [180, 180, 180],
        lineWidth: 0.1,
      },
      bodyStyles: {
        lineColor: [205, 205, 205],
        lineWidth: 0.1,
        textColor: [25, 25, 25],
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      margin: {
        left: M_LEFT,
        right: M_RIGHT,
        top: M_TOP + HEADER_H + 6,
        bottom: M_BOTTOM + FOOTER_H,
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 86 },
        2: { cellWidth: 45 },
        3: { cellWidth: 24 },
        4: { cellWidth: 18 },
        5: { cellWidth: 28, halign: "right" },
        6: { cellWidth: 28, halign: "right" },
        7: { cellWidth: 28, halign: "right" },
      },
      didDrawPage: (d) => {
        drawHeader();
        if (d.pageNumber === 1) drawResumen(M_TOP + HEADER_H + 8);
      },
      didParseCell: (hookData) => {
        if (hookData.section !== "body") return;

        if (filasSaldadas.has(hookData.row.index)) {
          hookData.cell.styles.textColor = [110, 18, 18];
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.fillColor = [255, 214, 214];
        }
      },
    });

    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      drawFooter(p, totalPages);
    }

    doc.save(
      `ctacte-${tipo}-${(entidadLabel || "entidad").replaceAll(
        " ",
        "_"
      )}-${desde}-${hasta}.pdf`
    );
  };

  return (
    <Container fluid="md" className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column gap-2">
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Tipo</Form.Label>
                  <Form.Select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                  >
                    <option value="CLIENTE">Cliente</option>
                    <option value="PROVEEDOR">Proveedor (Transporte)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={5}>
                <Form.Group>
                  <Form.Label className="mb-1">
                    {tipo === "CLIENTE" ? "Cliente" : "Transporte / Tercero"}
                  </Form.Label>
                  <Form.Select
                    value={entidadId}
                    onChange={(e) => setEntidadId(e.target.value)}
                  >
                    <option value="">— Seleccionar —</option>
                    {listaFiltrada.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="mb-1">Buscar en lista</Form.Label>
                  <Form.Control
                    placeholder="Escribí para filtrar…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={desde}
                    onChange={(e) => setDesde(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label className="mb-1">Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={hasta}
                    onChange={(e) => setHasta(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex gap-2 justify-content-md-end">
                <Button onClick={consultar} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" /> Consultando…
                    </>
                  ) : (
                    "Consultar"
                  )}
                </Button>

                <Button
                  variant="secondary"
                  onClick={exportarPDF}
                  disabled={!data || loading}
                >
                  Exportar PDF
                </Button>

                <Button
                  variant="outline-primary"
                  onClick={() => setShowSaldo(true)}
                  disabled={!entidadId}
                >
                  Saldo inicial
                </Button>

                <Button
                  variant="outline-dark"
                  onClick={() => setShowExtra(true)}
                  disabled={!entidadId}
                >
                  Cobro/Pago
                </Button>
              </Col>
            </Row>

            {err ? (
              <Alert className="mt-2 mb-0" variant="danger">
                {err}
              </Alert>
            ) : null}
          </div>
        </Card.Body>
      </Card>

      {!data ? (
        <div className="mt-3 text-muted">
          Elegí entidad + rango y tocá <b>Consultar</b>.
        </div>
      ) : (
        <>
          <div className="mt-3 text-muted">
            <b>{tipo === "CLIENTE" ? "Cliente" : "Transporte"}:</b>{" "}
            {entidadLabel || "(sin nombre)"} · <b>Período:</b> {desde} a {hasta}
          </div>

          <Row className="mt-2 g-2">
            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Saldo inicial
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(s0.saldo)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Debe ({tipo === "CLIENTE" ? "comandas" : "neto a tercero"})
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(tot.debe)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Haber ({tipo === "CLIENTE" ? "cobros" : "pagos"})
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(tot.haber)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Saldo final
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 22, lineHeight: 1.1 }}
                  >
                    $ {fmtARS(sf.saldo)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {tipo === "TRANSPORTE" && (
            <Row className="mt-2 g-2">
              {[
                { label: "Pactado bruto del período", valor: tot.pactadoBruto },
                { label: "Comisiones del período", valor: tot.comision },
                { label: "Merma del período", valor: tot.merma },
              ].map(({ label, valor }) => (
                <Col xs={12} md={4} key={label}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3 text-danger">
                      <div style={{ fontSize: 12 }}>{label}</div>
                      <div
                        className="fw-bold"
                        style={{ fontSize: 22, lineHeight: 1.1 }}
                      >
                        $ {fmtARS(valor)}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Movimientos</h5>

              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{ tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Fecha</th>
                      <th style={{ width: 235 }}>Concepto</th>
                      <th style={{ width: 145 }}>Chofer</th>
                      <th style={{ width: 105 }}>Factura</th>
                      <th style={{ width: 80 }}>Nro Viaje</th>
                      <th style={{ width: 120, textAlign: "right" }}>Debe</th>
                      <th style={{ width: 120, textAlign: "right" }}>Haber</th>
                      <th style={{ width: 120, textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {movs.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center">
                          Sin movimientos.
                        </td>
                      </tr>
                    ) : (
                      movs.map((m, idx) => {
                        const debe = Number(m?.debe || 0);
                        const haber = Number(m?.haber || 0);
                        const kind = String(m?.kind || "").toUpperCase();

                        const isPagoCobro = kind === "PAGO" || kind === "COBRO";
                        const isSaldado = esMovimientoSaldado(m, idx);

                        const cellStyle = isSaldado
                          ? {
                              color: "#6e1212",
                              fontWeight: 700,
                              backgroundColor: "#ffd6d6",
                            }
                          : isPagoCobro
                          ? {
                              backgroundColor: "#eaf3ff",
                            }
                          : undefined;

                        const cellBaseStyle = cellStyle || {};

                        return (
                          <tr key={m?.refId || `${m?.kind}-${idx}`}>
                            <td style={cellStyle}>{fmtFechaBA(m)}</td>

                            <td style={cellStyle}>
                              <div
                                className={isSaldado ? "fw-bold" : "fw-semibold"}
                              >
                                {conceptoUI(m)}
                              </div>
                            </td>

                            <td style={cellStyle}>{getChoferUI(m)}</td>

                            <td style={cellStyle}>{getFacturaUI(m)}</td>

                            <td
                              style={{
                                ...cellBaseStyle,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getNroViajeKey(m)}
                            </td>

                            <td
                              style={{
                                ...cellBaseStyle,
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {debe ? `$ ${fmtARS(debe)}` : ""}
                            </td>

                            <td
                              style={{
                                ...cellBaseStyle,
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {haber ? `$ ${fmtARS(haber)}` : ""}
                            </td>

                            <td
                              style={{
                                ...cellBaseStyle,
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              $ {fmtARS(m?.saldo)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}

      <ModalSaldoInicial
        show={showSaldo}
        onClose={() => setShowSaldo(false)}
        defaultTipo={tipo === "CLIENTE" ? "CLIENTE" : "TRANSPORTE"}
        defaultEntidadId={entidadId}
        defaultFecha={dayBefore(desde)}
        onSaved={() => {
          setShowSaldo(false);
          if (entidadId && desde && hasta) consultar();
        }}
      />

      <ModalCobroPagoExtraEntidad
        show={showExtra}
        onClose={() => setShowExtra(false)}
        defaultTipo={tipo === "CLIENTE" ? "CLIENTE" : "TRANSPORTE"}
        defaultEntidadId={entidadId}
        defaultFecha={hasta || todayYYYYMMDD()}
        onSaved={() => {
          setShowExtra(false);
          if (entidadId && desde && hasta) consultar();
        }}
      />
    </Container>
  );
}